<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title class="textwithbackground--text">
              {{ title }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <general-error v-if="!!generalError" />
            <v-progress-linear :active="isLoading" color="loader" indeterminate />
            <v-col>
              <lot-info-view
                  :lot="lot"
                  attachments
                  :sessions="isRegistration"
                  :positions="isRegistration">
                <template v-slot:attachments>
                  <business-document-list-view
                      :request-files="attachedFiles"
                      :document-types="requiredDocumentTypes"
                      :loading="isLoading" />
                </template>
                <template v-slot:positions>
                  <positions-list
                      v-if="lot.lotId"
                      :lot="lot"
                      :loading="isLoading"
                      :positions="positions" />
                </template>
                <template v-slot:sessions>
                  <sessions-list
                      v-if="lot.lotId"
                      :sessions="sessions"
                      :positions-count="positions.length"
                      :lot="lot"
                      readonly
                      :loading="isLoading" />
                </template>
              </lot-info-view>
            </v-col>
            <v-col class="mt-3">
              <ValidationObserver ref="comment">
                <validatable-textarea
                    v-if="inprocess || request.processComment"
                    v-model="processComment"
                    v-test="{ input: 'comment ' }"
                    :readonly="!inprocess"
                    :label="$t('Комментарий')"
                    :rules="{ required: rejected && inprocess }" />
              </ValidationObserver>
            </v-col>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col class="text-left">
                <route-btn-tip
                    :id="requestId"
                    :x-large="$vuetify.breakpoint.smAndUp"
                    :large="$vuetify.breakpoint.smAndDown"
                    :tip="$t('К списку запросов')"
                    icon="reply"
                    icon-color="primary"
                    :to="{ path: `${urlRootPath}/tradeRequests/${requestType}/${request.status}` }" />
              </v-col>
              <v-col
                  class="text-right">
                <btn-icon-tip
                    v-if="inprocess"
                    id="reject"
                    :btn-color="$vuetify.breakpoint.smAndUp
                      ? 'error'
                      : ''"
                    :icon-color="$vuetify.breakpoint.xsOnly
                      ? 'error'
                      : ''"
                    :icon="$vuetify.breakpoint.xsOnly
                      ? 'highlight_off'
                      : ''"
                    :disabled="isLoading"
                    :tip="$t('Отклонить')"
                    @click="reject" />
              </v-col>
              <v-col
                  class="text-right"
                  cols="3"
                  lg="2"
                  md="2"
                  sm="3">
                <btn-icon-tip
                    v-if="inprocess"
                    id="approve"
                    :btn-color="$vuetify.breakpoint.smAndUp
                      ? 'success'
                      : ''"
                    :icon-color="$vuetify.breakpoint.xsOnly
                      ? 'success'
                      : ''"
                    :icon="$vuetify.breakpoint.xsOnly
                      ? 'check_circle'
                      : ''"
                    :disabled="isLoading"
                    :tip="$t('Одобрить')"
                    @click="approve" />
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import RouteBtnTip from '@/components/reused/ui/RouteBtnTip.vue'
  import ValidatableTextarea from '@/components/reused/ui/ValidatableTextarea.vue'
  import LotInfoView from '@/components/reused/lot/LotInfoView.vue'
  import BusinessDocumentListView from '@/components/reused/files/BusinessDocumentListView.vue'
  import SessionsList from '@/components/reused/sessions/SessionsList.vue'
  import PositionsList from '@/components/reused/positions/PositionsList.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { LOT_REQUEST_TYPE_ENUM } from '@/constants/enums'
  import { LOT_REQUEST_STATUSES, LOT_REQUEST_TYPES, REQUEST_STATUSES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import {
    TRADE_REQUEST_MANAGEMENT, LOT_MANAGEMENT,
    LOT_SESSIONS_MANAGEMENT, LOT_POSITIONS_MANAGEMENT,
    BUSINESS_FILES
  } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      RouteBtnTip,
      ValidatableTextarea,
      LotInfoView,
      BusinessDocumentListView,
      SessionsList,
      PositionsList,
      GeneralError
    },
    props: {
      requestId: {
        type: String,
        required: true
      },
      requestType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        isLoading: false,
        processComment: null,
        rejected: false
      }
    },
    computed: {
      ...mapState(TRADE_REQUEST_MANAGEMENT.NAMESPACE, {
        request: state => (state[TRADE_REQUEST_MANAGEMENT.STATE.CURRENT_REQUEST] || {})
      }),
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        lot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT] || {})
      }),
      ...mapState(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        sessions: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.SESSIONS] || [])
      }),
      ...mapState(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        positions: state => (state[LOT_POSITIONS_MANAGEMENT.STATE.POSITIONS] || [])
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        requiredDocumentTypes: state => (state[BUSINESS_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      title () {
        return this.$t(this.inprocess
                         ? 'Обработка запроса по торговой процедуре ({0})'
                         : 'Просмотр запроса по торговой процедуре ({0})',
                       [this.$t(LOT_REQUEST_TYPE_ENUM[this.requestType])])
      },
      isRegistration () {
        return this.requestType === LOT_REQUEST_TYPES.REGISTRATION
      },
      inprocess () {
        return (this.request.status === LOT_REQUEST_STATUSES.INPROCESS && this.isAdmin)
      },
      attachedFiles () {
        return this.request.files || []
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(TRADE_REQUEST_MANAGEMENT.NAMESPACE, {
        getTradeRequestAdminAction: TRADE_REQUEST_MANAGEMENT.ACTIONS.GET_TRADE_REQUEST_ADMIN,
        processTradeRequestAction: TRADE_REQUEST_MANAGEMENT.ACTIONS.PROCESS_TRADE_REQUEST,
        clearTradeRequestAdminAction: TRADE_REQUEST_MANAGEMENT.ACTIONS.CLEAR_TRADE_REQUEST_ADMIN
      }),
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        getSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSIONS_LIST,
        clearSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSIONS_LIST
      }),
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        getPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.GET_POSITIONS_LIST,
        clearPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.CLEAR_POSITIONS_LIST
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        getLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES,
        clearLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearTradeRequestAdminAction(),
            this.clearLotRequestRequiredDocumentTypesAction(),
            this.clearSessionsListAction(),
            this.clearPositionsListAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        try {
          await this.getTradeRequestAdminAction({
            requestType: this.requestType,
            requestId: this.requestId
          })
          this.processComment = this.request.processComment
          const model = {
            requestType: this.requestType,
            lotType: this.request.lotType,
            lotId: this.request.lotId
          }
          await Promise.all([
            this.getLotInfoAction(model),
            this.getLotRequestRequiredDocumentTypesAction(model)
          ])
          if (this.isRegistration) {
            await Promise.all([
              this.getSessionsListAction(model),
              this.getPositionsListAction(model)
            ])
          }
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      approve: function () {
        const vm = this
        vm.rejected = false
        // nextTick: действия после перерисовки разметки (проверка полей)

        vm.$nextTick(() => {
          vm.processTradeRequest()
        })
      },
      reject: function () {
        const vm = this
        vm.rejected = true
        // nextTick: действия после перерисовки разметки (проверка полей)

        vm.$nextTick(() => {
          vm.processTradeRequest()
        })
      },
      processTradeRequest: async function () {
        const result = await this.$refs.comment.validate()
        if (!result) {
          return
        }
        try {
          this.isLoading = true
          await this.processTradeRequestAction({
            requestId: this.requestId,
            requestType: this.requestType,
            process: {
              processComment: this.processComment,
              status: this.rejected
                ? REQUEST_STATUSES.REJECTED
                : REQUEST_STATUSES.APPROVED
            }
          })
          this.isLoading = false
          this.goToRoute(`${this.urlRootPath}/tradeRequests/${this.requestType}/${this.request.status}`)
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }

  }
</script>
